import React, { useEffect, useRef, useState } from "react";
import Footer from "./Footer";
import Header from "./Header";
import axios from "axios";
import moment from "moment";
import config from "../../config";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  ArcElement,
  DoughnutController,
} from "chart.js";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  ArcElement,
  DoughnutController
);
const Index = () => {
  const kpiChartRefs = useRef({});
  const ks1Ref = useRef(null);
  const ks2Ref = useRef(null);
  const ks3Ref = useRef(null);
  const ks4Ref = useRef(null);
  const ks5Ref = useRef(null);
  const ks6Ref = useRef(null);
  const ks7Ref = useRef(null);
  const ks8Ref = useRef(null);
  const ks9Ref = useRef(null);
  const heroChartRef = useRef(null);
  const salesChartRef = useRef(null);
  const costChartRef = useRef(null);
  const perfChartRef = useRef(null);
  const donutChartRef = useRef(null);

  const userDetail = localStorage.getItem("ship_rocket_user");

  let userData = {};

  if (userDetail) {
    try {
      userData = JSON.parse(userDetail);
    } catch (error) {
      console.error("Invalid user data in localStorage:", error);
    }
  }

  const [userState, setUserState] = useState({});
  const user = () => {
    const dataToSend = {};
    const url = config.apiUrl + "/user/find_customer_detail";

    axios
      .post(url, dataToSend, { headers: config.headers })
      .then((res) => {
        if (res.data?.output) {
          setUserState(res.data.output);
        }
      })
      .catch((error) => {
        console.log("Error fetching customer details:", error);
      });
  };
  useEffect(() => {
    user();
  }, []);

  const getKycStatus = () => {
    if (userState?.kyc === true) {
      return {
        type: "verified",
        title: "Your account is verified",
        description:
          "Your KYC has been successfully verified. You can now use all available shipping features.",
        label: "Verified",
        icon: "bi-patch-check-fill",
        buttonText: "View KYC",
      };
    }

    if (Number(userState?.alert_kyc) === 1) {
      return {
        type: "review",
        title: "Your KYC is under review",
        description:
          "Your KYC details and documents have been submitted successfully. Our team is reviewing your application.",
        label: "Under Review",
        icon: "bi-clock-history",
        buttonText: "View KYC",
      };
    }

    return {
      type: "pending",
      title: "Verify your account to unlock full shipping",
      description:
        "Your KYC is pending. Complete verification to enable prepaid billing, higher shipment limits and more carrier options.",
      label: "Pending",
      icon: "bi-clock-history",
      buttonText: "Start KYC",
    };
  };

  const kycStatus = getKycStatus();

  // --------------------------------------------------
  // DATE RANGE
  // --------------------------------------------------

  const [dateRange, setDateRange] = useState({
    from: moment().subtract(120, "days").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  });

  // --------------------------------------------------
  // DASHBOARD STATE
  // --------------------------------------------------

  const [dashboardData, setDashboardData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [transactionData, setTransactionData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [activeView, setActiveView] = useState("dash");
  const [heroMetric, setHeroMetric] = useState("shipments");
  const [salesMetric, setSalesMetric] = useState("sales");
  const [perfType, setPerfType] = useState("all");
  const [perfCarrier, setPerfCarrier] = useState("all");
  const [perfWarehouse, setPerfWarehouse] = useState("all");

  // --------------------------------------------------
  // GREETING
  // --------------------------------------------------

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return "Good Morning";
    }

    if (hour >= 12 && hour < 17) {
      return "Good Afternoon";
    }

    return "Good Evening";
  };

  // --------------------------------------------------
  // DASHBOARD API
  // --------------------------------------------------

  const getDashboardData = async () => {
    try {
      if (!userData?.customer_id) {
        setDashboardError("Customer information not found.");
        return;
      }
      if (!dateRange.from || !dateRange.to) {
        setDashboardError("Please select a valid date range.");
        return;
      }
      if (dateRange.from > dateRange.to) {
        setDashboardError("From date cannot be greater than To date.");
        return;
      }

      setDashboardLoading(true);
      setDashboardError("");

      const payload = {
        customer_id: userData.customer_id,
        date: [dateRange.from, dateRange.to],
      };

      const [dashboardRes, summaryRes, graphRes] = await Promise.all([
        axios.post(config.apiUrl + "/home/all_dashboard_data", payload, {
          headers: config.headers,
        }),
        axios.post(
          config.apiUrl + "/home/dashboard_api",
          {
            customer_id: userData.customer_id,
            start_date: dateRange.from,
            end_date: dateRange.to,
          },
          { headers: config.headers }
        ),
        axios.post(config.apiUrl + "/home/graph_dashboard_data", payload, {
          headers: config.headers,
        }),
      ]);

      if (dashboardRes.data?.status === true) {
        setDashboardData(
          Array.isArray(dashboardRes.data.output)
            ? dashboardRes.data.output
            : []
        );
      } else {
        setDashboardData([]);
      }
      setSummaryData(summaryRes.data?.status === true ? summaryRes.data : {});
      setGraphData(
        graphRes.data?.status === true && Array.isArray(graphRes.data.output)
          ? graphRes.data.output
          : []
      );
      setTransactionData(
        graphRes.data?.status === true &&
          Array.isArray(graphRes.data.transactions)
          ? graphRes.data.transactions
          : []
      );

      if (dashboardRes.data?.status !== true) {
        setDashboardError(
          dashboardRes.data?.message || "Unable to fetch dashboard data."
        );
      }
    } catch (error) {
      console.error("Dashboard API Error ===>", error);
      setDashboardData([]);
      setSummaryData({});
      setGraphData([]);
      setTransactionData([]);
      setDashboardError(
        error?.response?.data?.message ||
          "Something went wrong while loading dashboard data."
      );
    } finally {
      setDashboardLoading(false);
    }
  };
  const shipments = Array.isArray(dashboardData) ? dashboardData : [];

  const num = (value) => Number(value) || 0;
  const statusOf = (s) => num(s?.status);
  const isRto = (s) => [10, 19, 16].includes(statusOf(s));
  const isActiveShipment = (s) => statusOf(s) !== 0 && statusOf(s) !== 7;
  const isRunning = (s) => [1, 2, 3, 4].includes(statusOf(s));
  const getReceiver = (s) =>
    Array.isArray(s?.receiver) ? s.receiver[0] || {} : s?.receiver || {};
  const getCity = (s) => getReceiver(s)?.city || s?.receiver_city || "Unknown";
  const getState = (s) =>
    getReceiver(s)?.state || s?.receiver_state || "Unknown";
  const getWarehouseKey = (s) =>
    String(
      s?.warehouse_id ||
        s?.warehouse_name ||
        s?.warehouse ||
        s?.address_id ||
        "Unknown"
    );
  const getWarehouseLabel = (s) =>
    s?.warehouse_name ||
    s?.warehouse ||
    s?.warehouse_id ||
    s?.address_id ||
    "Unknown";
  const getCarrier = (s) => s?.carrier_name || s?.service_provider || "Unknown";
  const getPaymentMode = (s) => String(s?.payment_mode || "").toLowerCase();
  const getWeightKg = (s) => num(s?.total_weight ?? s?.dead_weight);
  const getSalesValue = (s) =>
    Array.isArray(s?.items)
      ? s.items.reduce(
          (sum, item) =>
            sum +
            num(item?.item_value) * Math.max(1, num(item?.item_quantity) || 1),
          0
        )
      : num(s?.cod_amount);
  const money = (value) =>
    `₹${num(value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const compactMoney = (value) => {
    const v = num(value);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return money(v);
  };
  const pct = (value, total) =>
    total ? `${((value / total) * 100).toFixed(1)}%` : "0.0%";
  const formatDate = (d) => moment(d).format("DD-MM-YYYY");

  const getKpiStats = () => {
    const total = shipments.filter(isActiveShipment).length;
    const pending = shipments.filter((s) => statusOf(s) === 1).length;
    const pickedUp = shipments.filter((s) => statusOf(s) === 2).length;
    const inTransit = shipments.filter((s) => statusOf(s) === 3).length;
    const outForDelivery = shipments.filter((s) => statusOf(s) === 4).length;
    const delivered = shipments.filter((s) => statusOf(s) === 5).length;
    const failed = shipments.filter((s) => statusOf(s) === 7).length;
    const returned = shipments.filter(isRto).length;
    const cancelled = shipments.filter((s) => statusOf(s) === 6).length;
    const percentage = (v) => pct(v, total);
    return {
      total,
      pending,
      pickedUp,
      inTransit,
      outForDelivery,
      delivered,
      failed,
      returned,
      cancelled,
      totalPercentage: total ? "100%" : "0.0%",
      pendingPercentage: percentage(pending),
      pickedUpPercentage: percentage(pickedUp),
      inTransitPercentage: percentage(inTransit),
      outForDeliveryPercentage: percentage(outForDelivery),
      deliveredPercentage: percentage(delivered),
      failedPercentage: percentage(failed),
      returnedPercentage: percentage(returned),
      cancelledPercentage: percentage(cancelled),
    };
  };

  const getDailyKpiData = (source = shipments) => {
    const rows = Array.isArray(source) ? source : [];

    const daily = {};

    const start = moment(dateRange.from);
    const end = moment(dateRange.to);

    // Create every date in selected range
    const current = start.clone();

    while (current.isSameOrBefore(end, "day")) {
      const date = current.format("YYYY-MM-DD");

      daily[date] = {
        total: 0,
        pending: 0,
        pickedUp: 0,
        inTransit: 0,
        outForDelivery: 0,
        delivered: 0,
        failed: 0,
        returned: 0,
        cancelled: 0,
      };

      current.add(1, "day");
    }

    rows.forEach((shipment) => {
      const date = shipment.shipment_created_date
        ? moment(shipment.shipment_created_date).format("YYYY-MM-DD")
        : shipment.created
        ? moment.unix(Number(shipment.created)).format("YYYY-MM-DD")
        : "";

      if (!daily[date]) {
        return;
      }

      const status = Number(shipment.status);

      // Same "All" logic used in Phase 2
      if (status !== 0 && status !== 7) {
        daily[date].total++;
      }

      if (status === 1) {
        daily[date].pending++;
      }

      if (status === 2) {
        daily[date].pickedUp++;
      }

      if (status === 3) {
        daily[date].inTransit++;
      }

      if (status === 4) {
        daily[date].outForDelivery++;
      }

      if (status === 5) {
        daily[date].delivered++;
      }

      if (status === 7) {
        daily[date].failed++;
      }

      if (status === 10 || status === 19 || status === 16) {
        daily[date].returned++;
      }

      if (status === 6) {
        daily[date].cancelled++;
      }
    });

    return daily;
  };

  const getKpiChartConfig = (source = graphData) => {
    const daily = getDailyKpiData(source);
    const dates = Object.keys(daily);
    return {
      labels: dates,
      datasets: {
        total: dates.map((d) => daily[d].total),
        pending: dates.map((d) => daily[d].pending),
        pickedUp: dates.map((d) => daily[d].pickedUp),
        inTransit: dates.map((d) => daily[d].inTransit),
        outForDelivery: dates.map((d) => daily[d].outForDelivery),
        delivered: dates.map((d) => daily[d].delivered),
        failed: dates.map((d) => daily[d].failed),
        returned: dates.map((d) => daily[d].returned),
        cancelled: dates.map((d) => daily[d].cancelled),
      },
    };
  };

  const getTrendRows = (source = graphData) => {
    const daily = {};
    const start = moment(dateRange.from);
    const end = moment(dateRange.to);
    const current = start.clone();

    while (current.isSameOrBefore(end, "day")) {
      const d = current.format("YYYY-MM-DD");
      daily[d] = { shipments: 0, delivered: 0, rto: 0, sales: 0, orders: 0 };
      current.add(1, "day");
    }

    (Array.isArray(source) ? source : []).forEach((s) => {
      const date = s?.shipment_created_date
        ? moment(s.shipment_created_date).format("YYYY-MM-DD")
        : s?.created
        ? moment.unix(num(s.created)).format("YYYY-MM-DD")
        : "";

      if (!daily[date]) return;

      const status = statusOf(s);
      if (isActiveShipment(s)) daily[date].shipments++;
      if (status === 5) daily[date].delivered++;
      if (isRto(s)) daily[date].rto++;
      daily[date].sales += getSalesValue(s);
      daily[date].orders++;
    });

    return daily;
  };

  const getHeroTrend = () => {
    const daily = getTrendRows(graphData);
    const labels = Object.keys(daily);
    const key =
      heroMetric === "delivered"
        ? "delivered"
        : heroMetric === "rto"
        ? "rto"
        : "shipments";

    return {
      labels,
      values: labels.map((date) => num(daily[date][key])),
      key,
    };
  };

  const getSalesTrend = () => {
    const daily = getTrendRows(graphData);
    const labels = Object.keys(daily);

    return {
      labels,
      values: labels.map((date) => {
        if (salesMetric === "orders") return daily[date].orders;
        if (salesMetric === "aov") {
          return daily[date].orders
            ? daily[date].sales / daily[date].orders
            : 0;
        }
        return daily[date].sales;
      }),
    };
  };

  const getPeriodMetrics = () => {
    const rows = Array.isArray(graphData) ? graphData : [];
    const active = rows.filter(isActiveShipment);
    const sales = active.reduce((sum, s) => sum + getSalesValue(s), 0);
    const orders = active.length;

    return {
      shipments: active.length,
      delivered: active.filter((s) => statusOf(s) === 5).length,
      rto: active.filter(isRto).length,
      sales,
      orders,
      aov: orders ? sales / orders : 0,
    };
  };

  const getCostData = () => {
    const daily = {};
    const rows = Array.isArray(graphData)
      ? graphData.filter(isActiveShipment)
      : [];

    rows.forEach((s) => {
      const date = s?.shipment_created_date
        ? moment(s.shipment_created_date).format("YYYY-MM-DD")
        : s?.created
        ? moment.unix(num(s.created)).format("YYYY-MM-DD")
        : "";

      if (!date) return;

      if (!daily[date]) daily[date] = { cost: 0, count: 0 };

      daily[date].cost += num(
        s?.total_amount ?? s?.rate_price ?? s?.sub_total ?? s?.shipping_charges
      );
      daily[date].count++;
    });

    const labels = Object.keys(daily).sort();
    const values = labels.map((date) =>
      daily[date].count ? daily[date].cost / daily[date].count : 0
    );

    const avg = rows.length
      ? rows.reduce(
          (sum, s) =>
            sum +
            num(
              s?.total_amount ??
                s?.rate_price ??
                s?.sub_total ??
                s?.shipping_charges
            ),
          0
        ) / rows.length
      : 0;

    return { labels, values, avg };
  };

  const getFilteredAnalyticsShipments = (source = graphData) => {
    return (Array.isArray(source) ? source : []).filter((s) => {
      if (perfType === "cod" && getPaymentMode(s) !== "cod") return false;
      if (perfType === "prepaid" && getPaymentMode(s) !== "prepaid")
        return false;
      if (perfCarrier !== "all" && getCarrier(s) !== perfCarrier) return false;
      if (perfWarehouse !== "all" && getWarehouseKey(s) !== perfWarehouse)
        return false;
      return true;
    });
  };

  const groupOverview = (field, source = graphData) => {
    const map = {};

    (Array.isArray(source) ? source : [])
      .filter(isActiveShipment)
      .forEach((s) => {
        const key =
          field === "carrier"
            ? getCarrier(s)
            : field === "state"
            ? getState(s)
            : field === "city"
            ? getCity(s)
            : s?.zone_name || s?.zone || "Unknown";

        if (!map[key]) {
          map[key] = {
            name: key,
            booked: 0,
            delivered: 0,
            running: 0,
            rto: 0,
            cod: 0,
            prepaid: 0,
          };
        }

        map[key].booked++;
        if (statusOf(s) === 5) map[key].delivered++;
        if (isRunning(s)) map[key].running++;
        if (isRto(s)) map[key].rto++;

        if (getPaymentMode(s) === "cod") map[key].cod++;
        if (getPaymentMode(s) === "prepaid") map[key].prepaid++;
      });

    return Object.values(map).sort((a, b) => b.booked - a.booked);
  };

  const getPaymentSummary = (source = graphData) => {
    const rows = groupOverview("carrier", source);
    const cod = rows.reduce((sum, row) => sum + row.cod, 0);
    const prepaid = rows.reduce((sum, row) => sum + row.prepaid, 0);

    return {
      rows,
      cod,
      prepaid,
      total: cod + prepaid,
    };
  };

  const getDeliveryFunnel = () => {
    const rows = Array.isArray(graphData) ? graphData : [];
    const booked = rows.filter(isActiveShipment).length;
    const picked = rows.filter((s) =>
      [2, 3, 4, 5].includes(statusOf(s))
    ).length;
    const transit = rows.filter((s) => [3, 4, 5].includes(statusOf(s))).length;
    const ofd = rows.filter((s) => [4, 5].includes(statusOf(s))).length;
    const delivered = rows.filter((s) => statusOf(s) === 5).length;

    return [
      ["Booked", booked],
      ["Picked Up", picked],
      ["In Transit", transit],
      ["Out for Delivery", ofd],
      ["Delivered", delivered],
    ].map(([name, value]) => ({
      name,
      value,
      percentage: pct(value, booked),
    }));
  };

  const getCodAnalytics = () => {
    const rows = Array.isArray(graphData) ? graphData : [];
    const cod = rows.filter((s) => getPaymentMode(s) === "cod");

    const collected = cod
      .filter((s) => statusOf(s) === 5)
      .reduce((sum, s) => sum + num(s?.cod_amount), 0);

    const settled = cod
      .filter((s) => s?.cod_paid === true)
      .reduce((sum, s) => sum + num(s?.cod_amount), 0);

    const pending = cod
      .filter((s) => statusOf(s) !== 5 && !isRto(s))
      .reduce((sum, s) => sum + num(s?.cod_amount), 0);

    return {
      collected,
      pending,
      settled,
      total: cod.reduce((sum, s) => sum + num(s?.cod_amount), 0),
    };
  };

  const getWeightDistribution = () => {
    const buckets = [
      { name: "0 - 500g", min: 0, max: 0.5 },
      { name: "500g - 1kg", min: 0.5, max: 1 },
      { name: "1 - 2kg", min: 1, max: 2 },
      { name: "2kg +", min: 2, max: Infinity },
    ];

    const rows = Array.isArray(graphData) ? graphData : [];
    const total = rows.filter(isActiveShipment).length;

    return buckets.map((bucket) => {
      const count = rows.filter((s) => {
        const weight = getWeightKg(s);
        return (
          isActiveShipment(s) && weight >= bucket.min && weight < bucket.max
        );
      }).length;

      return {
        ...bucket,
        count,
        percentage: Number(pct(count, total).replace("%", "")) || 0,
      };
    });
  };

  const getNdrSummary = () => {
    const result = {
      "Customer Unreachable": 0,
      "Address Issue": 0,
      Rescheduled: 0,
      Rejected: 0,
    };

    (Array.isArray(graphData) ? graphData : []).forEach((s) => {
      const arr = Array.isArray(s?.ndr_array)
        ? s.ndr_array
        : Array.isArray(s?.ndrArray)
        ? s.ndrArray
        : [];

      arr.forEach((n) => {
        const text = `${n?.action || ""} ${n?.remarks || ""} ${
          n?.updated_details || ""
        }`.toLowerCase();

        if (/unreach|not reachable|no answer/.test(text)) {
          result["Customer Unreachable"]++;
        } else if (/address|location|pincode|pin code/.test(text)) {
          result["Address Issue"]++;
        } else if (/resched|later|tomorrow/.test(text)) {
          result.Rescheduled++;
        } else if (/reject|refus|deny/.test(text)) {
          result.Rejected++;
        }
      });
    });

    return result;
  };

  // --------------------------------------------------
  // CHARTS
  // --------------------------------------------------
  // Chart rendering is implemented only in this page.
  // elite.js is a reference only and is not imported or executed.

  const destroyChart = (ref) => {
    if (ref?.current) {
      ref.current.destroy();
      ref.current = null;
    }
  };

  const getCanvas = (id) => document.getElementById(id);

  const getChartGradient = (canvas, top, bottom) => {
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height || 240);
    gradient.addColorStop(0, top);
    gradient.addColorStop(1, bottom);
    return gradient;
  };

  const createKpiSparkline = (ref, canvasId, labels, data) => {
    const canvas = getCanvas(canvasId);
    if (!canvas) return;
    destroyChart(ref);
    ref.current = new Chart(canvas.getContext("2d"), {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            data,
            borderColor: "#64748b",
            backgroundColor: "rgba(100,116,139,.10)",
            borderWidth: 1.8,
            pointRadius: 0,
            pointHoverRadius: 3,
            tension: 0.4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: (items) => items?.[0]?.label || "",
              label: (context) =>
                ` ${Number(context.raw || 0).toLocaleString("en-IN")}`,
            },
          },
        },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
      },
    });
  };

  const createHeroChart = () => {
    const canvas = getCanvas("heroChart");
    if (!canvas) return;
    destroyChart(heroChartRef);
    const hero = getHeroTrend();
    const metricConfig = {
      shipments: {
        label: "Shipments",
        borderColor: "#10b981",
        fillTop: "rgba(16,185,129,.22)",
        fillBottom: "rgba(16,185,129,0)",
      },
      delivered: {
        label: "Delivered",
        borderColor: "#3b82f6",
        fillTop: "rgba(59,130,246,.22)",
        fillBottom: "rgba(59,130,246,0)",
      },
      rto: {
        label: "RTO",
        borderColor: "#f59e0b",
        fillTop: "rgba(245,158,11,.22)",
        fillBottom: "rgba(245,158,11,0)",
      },
    };
    const config = metricConfig[hero.key] || metricConfig.shipments;
    heroChartRef.current = new Chart(canvas.getContext("2d"), {
      type: "line",
      data: {
        labels: hero.labels.map((date) => moment(date).format("DD MMM")),
        datasets: [
          {
            label: config.label,
            data: hero.values,
            borderColor: config.borderColor,
            backgroundColor: getChartGradient(
              canvas,
              config.fillTop,
              config.fillBottom
            ),
            borderWidth: 2.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: config.borderColor,
            pointHoverBorderColor: "#fff",
            tension: 0.4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            backgroundColor: "#1e293b",
            bodyColor: "#fff",
            titleColor: "#fff",
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (context) =>
                ` ${config.label}: ${Number(context.raw || 0).toLocaleString(
                  "en-IN"
                )}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: "#64748b", font: { size: 11 }, maxTicksLimit: 8 },
          },
          y: {
            beginAtZero: true,
            suggestedMax: Math.max(...hero.values, 0) || 10,
            grid: { color: "rgba(148,163,184,.16)" },
            border: { display: false },
            ticks: {
              color: "#64748b",
              font: { size: 11 },
              precision: 0,
              callback: (value) => Number(value).toLocaleString("en-IN"),
            },
          },
        },
      },
    });
  };

  const createSalesChart = () => {
    const canvas = getCanvas("salesChart");
    if (!canvas) return;
    destroyChart(salesChartRef);
    const sales = getSalesTrend();
    const isMoney = salesMetric !== "orders";
    const label =
      salesMetric === "sales"
        ? "Sales"
        : salesMetric === "orders"
        ? "Orders"
        : "AOV";
    salesChartRef.current = new Chart(canvas.getContext("2d"), {
      type: "line",
      data: {
        labels: sales.labels.map((date) => moment(date).format("DD MMM")),
        datasets: [
          {
            label,
            data: sales.values,
            borderColor: "#059669",
            backgroundColor: getChartGradient(
              canvas,
              "rgba(16,185,129,.20)",
              "rgba(16,185,129,0)"
            ),
            borderWidth: 2.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            tension: 0.4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: (context) =>
                ` ${label}: ${
                  isMoney
                    ? money(context.raw)
                    : Number(context.raw || 0).toLocaleString("en-IN")
                }`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: "#64748b", font: { size: 11 }, maxTicksLimit: 8 },
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(148,163,184,.16)" },
            border: { display: false },
            ticks: {
              color: "#64748b",
              font: { size: 11 },
              callback: (value) =>
                isMoney
                  ? compactMoney(value)
                  : Number(value).toLocaleString("en-IN"),
            },
          },
        },
      },
    });
  };

  const createCostChart = () => {
    const canvas = getCanvas("costChart");
    if (!canvas) return;
    destroyChart(costChartRef);
    const cost = getCostData();
    costChartRef.current = new Chart(canvas.getContext("2d"), {
      type: "line",
      data: {
        labels: cost.labels.map((date) => moment(date).format("DD MMM")),
        datasets: [
          {
            label: "Average shipping cost",
            data: cost.values,
            borderColor: "#d97706",
            backgroundColor: getChartGradient(
              canvas,
              "rgba(245,158,11,.20)",
              "rgba(245,158,11,0)"
            ),
            borderWidth: 2.2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.35,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: (context) => ` Average: ${money(context.raw)}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: "#64748b", font: { size: 11 }, maxTicksLimit: 8 },
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(148,163,184,.16)" },
            border: { display: false },
            ticks: {
              color: "#64748b",
              font: { size: 11 },
              callback: (value) => money(value),
            },
          },
        },
      },
    });
  };

  const createPerformanceChart = () => {
    const canvas = getCanvas("anPerfLine");
    if (!canvas) return;
    destroyChart(perfChartRef);
    const rows = getFilteredAnalyticsShipments(graphData);
    const daily = getDailyKpiData(rows);
    const labels = Object.keys(daily);
    perfChartRef.current = new Chart(canvas.getContext("2d"), {
      type: "line",
      data: {
        labels: labels.map((date) => moment(date).format("DD MMM")),
        datasets: [
          {
            label: "Booked",
            data: labels.map((date) => daily[date].total),
            borderColor: "#3b82f6",
            backgroundColor: "rgba(59,130,246,.08)",
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.35,
            fill: true,
          },
          {
            label: "Delivered",
            data: labels.map((date) => daily[date].delivered),
            borderColor: "#10b981",
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.35,
            fill: false,
          },
          {
            label: "RTO",
            data: labels.map((date) => daily[date].returned),
            borderColor: "#f59e0b",
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.35,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { usePointStyle: true, boxWidth: 8 },
          },
          tooltip: {
            callbacks: {
              label: (context) =>
                ` ${context.dataset.label}: ${Number(
                  context.raw || 0
                ).toLocaleString("en-IN")}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: "#64748b", font: { size: 11 }, maxTicksLimit: 10 },
          },
          y: {
            beginAtZero: true,
            grid: { color: "rgba(148,163,184,.16)" },
            border: { display: false },
            ticks: {
              color: "#64748b",
              precision: 0,
              callback: (value) => Number(value).toLocaleString("en-IN"),
            },
          },
        },
      },
    });
  };

  const createOrderDonut = () => {
    const canvas = getCanvas("anOrderDonut");
    if (!canvas) return;
    destroyChart(donutChartRef);
    const p = getPaymentSummary(graphData);
    donutChartRef.current = new Chart(canvas.getContext("2d"), {
      type: "doughnut",
      data: {
        labels: ["COD", "Prepaid"],
        datasets: [
          { data: [p.cod, p.prepaid], borderWidth: 0, hoverOffset: 4 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          legend: {
            position: "bottom",
            labels: { usePointStyle: true, boxWidth: 8 },
          },
          tooltip: {
            callbacks: {
              label: (context) =>
                ` ${context.label}: ${Number(context.raw || 0).toLocaleString(
                  "en-IN"
                )} (${pct(Number(context.raw || 0), p.total || 1)})`,
            },
          },
        },
      },
    });
  };

  const renderKpiCharts = () => {
    const chartData = getKpiChartConfig(graphData);
    const charts = [
      { id: "ks1", key: "total", ref: ks1Ref },
      { id: "ks2", key: "pending", ref: ks2Ref },
      { id: "ks3", key: "pickedUp", ref: ks3Ref },
      { id: "ks4", key: "inTransit", ref: ks4Ref },
      { id: "ks5", key: "outForDelivery", ref: ks5Ref },
      { id: "ks6", key: "delivered", ref: ks6Ref },
      { id: "ks7", key: "failed", ref: ks7Ref },
      { id: "ks8", key: "returned", ref: ks8Ref },
      { id: "ks9", key: "cancelled", ref: ks9Ref },
    ];
    charts.forEach(({ id, key, ref }) =>
      createKpiSparkline(ref, id, chartData.labels, chartData.datasets[key])
    );
  };

  const renderMainCharts = () => {
    createHeroChart();
    createSalesChart();
    createCostChart();
    createPerformanceChart();
    createOrderDonut();
  };

  useEffect(() => {
    if (!dashboardData.length && !graphData.length) return;

    const timer = setTimeout(() => {
      renderKpiCharts();
      renderMainCharts();
    }, 0);

    return () => {
      clearTimeout(timer);
      [
        heroChartRef,
        salesChartRef,
        costChartRef,
        perfChartRef,
        donutChartRef,
        ks1Ref,
        ks2Ref,
        ks3Ref,
        ks4Ref,
        ks5Ref,
        ks6Ref,
        ks7Ref,
        ks8Ref,
        ks9Ref,
      ].forEach(destroyChart);
    };
  }, [
    dashboardData,
    graphData,
    dateRange,
    heroMetric,
    salesMetric,
    perfType,
    perfCarrier,
    perfWarehouse,
  ]);

  // --------------------------------------------------
  // INITIAL DASHBOARD LOAD
  // --------------------------------------------------

  useEffect(() => {
    getDashboardData();
  }, []);
  const kpiStats = getKpiStats();
  return (
    <div>
      <Header>
        <div className="content">
          <div class="header-row d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div>
              <div class="page-title">
                {" "}
                {getGreeting()}, {userData?.full_name || ""} 👋
              </div>
              <div class="page-sub">
                Today:{" "}
                {Number(summaryData.today_shipments || 0).toLocaleString(
                  "en-IN"
                )}{" "}
                shipments ·{" "}
                {Number(summaryData.today_orders || 0).toLocaleString("en-IN")}{" "}
                orders
              </div>
            </div>
            <div className="view-tabs head-center">
              <button
                type="button"
                className={`vt ${activeView === "dash" ? "on" : ""}`}
                onClick={() => setActiveView("dash")}
              >
                <i className="bi bi-speedometer2"></i> Dashboard
              </button>
              <button
                type="button"
                className={`vt ${activeView === "analytics" ? "on" : ""}`}
                onClick={() => setActiveView("analytics")}
              >
                <i className="bi bi-bar-chart-line"></i> Analytics
              </button>
            </div>
            <div class="dr-picker" id="drPicker">
              <div class="dr-field">
                <span class="dr-label">From</span>
                <input
                  type="date"
                  className="dr-input"
                  id="drFrom"
                  value={dateRange.from}
                  onChange={(e) => {
                    setDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }));
                  }}
                />
              </div>
              <span class="dr-sep">&rarr;</span>
              <div class="dr-field">
                <span class="dr-label">To</span>
                <input
                  type="date"
                  className="dr-input"
                  id="drTo"
                  value={dateRange.to}
                  onChange={(e) => {
                    setDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }));
                  }}
                />
              </div>
              <button
                type="button"
                className="dr-apply"
                id="drApply"
                onClick={getDashboardData}
                disabled={dashboardLoading}
              >
                <i className="bi bi-arrow-repeat"></i>

                {dashboardLoading ? " Loading..." : " Apply"}
              </button>
            </div>
          </div>

          <div
            id="viewDash"
            style={{ display: activeView === "dash" ? "block" : "none" }}
          >
            {dashboardLoading && (
              <div className="card-base mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  <span>Loading dashboard data...</span>
                </div>
              </div>
            )}
            {dashboardError && (
              <div className="alert alert-danger mb-3">{dashboardError}</div>
            )}
            <div className={`card-base kyc-alert mb-3 ${kycStatus.type}`}>
              <span className="kyc-ico">
                <i className={`bi ${kycStatus.icon}`}></i>
              </span>

              <div className="kyc-body">
                <div className="kyc-title">{kycStatus.title}</div>

                <div className="kyc-desc">{kycStatus.description}</div>

                <span className="kyc-chip">
                  <i className={`bi ${kycStatus.icon}`}></i> Status:{" "}
                  {kycStatus.label}
                </span>
              </div>

              <a href="/kyc" className="kyc-cta-btn">
                {kycStatus.buttonText} <i className="bi bi-arrow-right"></i>
              </a>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-xl-6">
                <div className="card-base hero-card">
                  <div className="hero-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(59,130,246,.12)",
                            color: "#3b82f6",
                          }}
                        >
                          <i className="bi bi-graph-up"></i>
                        </span>{" "}
                        Shipment Volume Trend
                      </div>
                      <div className="dash-sub">
                        Daily shipments across the network
                      </div>
                    </div>
                    <div className="metric-tabs">
                      {[
                        ["shipments", "Shipments"],
                        ["delivered", "Delivered"],
                        ["rto", "RTO"],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={`mt ${heroMetric === key ? "on" : ""}`}
                          onClick={() => setHeroMetric(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="hero-val">
                      {num(getPeriodMetrics()[heroMetric]).toLocaleString(
                        "en-IN"
                      )}
                    </div>
                    <div className="stat-trend hero-delta up">
                      <i className="bi bi-activity"></i> Selected period
                    </div>
                    <div className="dash-sub">
                      {heroMetric === "delivered"
                        ? "Delivered shipments in period"
                        : heroMetric === "rto"
                        ? "Return to origin shipments in period"
                        : "Total shipments in period"}
                    </div>
                  </div>
                  <div className="hero-chart">
                    <canvas id="heroChart"></canvas>
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="card-base hero-card">
                  <div className="hero-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(16,185,129,.12)",
                            color: "#059669",
                          }}
                        >
                          <i className="bi bi-cart-check"></i>
                        </span>{" "}
                        Sales Volume Trend
                      </div>
                      <div className="dash-sub">
                        Daily order value across channels
                      </div>
                    </div>
                    <div className="metric-tabs">
                      {[
                        ["sales", "Sales"],
                        ["orders", "Orders"],
                        ["aov", "AOV"],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={`mt ${salesMetric === key ? "on" : ""}`}
                          onClick={() => setSalesMetric(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="hero-val">
                      {salesMetric === "sales"
                        ? compactMoney(getPeriodMetrics().sales)
                        : salesMetric === "orders"
                        ? getPeriodMetrics().orders.toLocaleString("en-IN")
                        : money(getPeriodMetrics().aov)}
                    </div>
                    <div className="stat-trend hero-delta up">
                      <i className="bi bi-activity"></i> Selected period
                    </div>
                    <div className="dash-sub">
                      {salesMetric === "sales"
                        ? "Total sales in period"
                        : salesMetric === "orders"
                        ? "Orders in period"
                        : "Average order value"}
                    </div>
                  </div>
                  <div className="hero-chart">
                    <canvas id="salesChart"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="kpi-strip mb-3">
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-blue">
                  <div className="kpi-top">
                    <span className="kpi-ico i-blue">
                      <i className="bi bi-box-seam"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.totalPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">All</div>
                  <div className="kpi-val">
                    {kpiStats.total.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">All shipments this period</div>
                  <div className="kpi-spark">
                    <canvas id="ks1"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-amber">
                  <div className="kpi-top">
                    <span className="kpi-ico i-amber">
                      <i className="bi bi-hourglass-split"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.pendingPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Pending</div>
                  <div className="kpi-val">
                    {kpiStats.pending.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">Awaiting pickup</div>
                  <div className="kpi-spark">
                    <canvas id="ks2"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-teal">
                  <div className="kpi-top">
                    <span className="kpi-ico i-teal">
                      <i className="bi bi-arrow-up-square"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.pickedUpPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Picked Up</div>
                  <div className="kpi-val">
                    {kpiStats.pickedUp.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">In carrier pickup</div>
                  <div className="kpi-spark">
                    <canvas id="ks3"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-violet">
                  <div className="kpi-top">
                    <span className="kpi-ico i-violet">
                      <i className="bi bi-truck"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.inTransitPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">In Transit</div>
                  <div className="kpi-val">
                    {kpiStats.inTransit.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">Currently moving</div>
                  <div className="kpi-spark">
                    <canvas id="ks4"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-sky">
                  <div className="kpi-top">
                    <span className="kpi-ico i-blue">
                      <i className="bi bi-truck-front"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.outForDeliveryPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Out for Del.</div>
                  <div className="kpi-val">
                    {kpiStats.outForDelivery.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">On the last mile</div>
                  <div className="kpi-spark">
                    <canvas id="ks5"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-green">
                  <div className="kpi-top">
                    <span className="kpi-ico i-green">
                      <i className="bi bi-check-circle"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.deliveredPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Delivered</div>
                  <div className="kpi-val">
                    {kpiStats.delivered.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">Delivery success</div>
                  <div className="kpi-spark">
                    <canvas id="ks6"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-red">
                  <div className="kpi-top">
                    <span className="kpi-ico i-red">
                      <i className="bi bi-exclamation-triangle"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.failedPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Failed</div>
                  <div className="kpi-val">
                    {kpiStats.failed.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">Attention required</div>
                  <div className="kpi-spark">
                    <canvas id="ks7"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-rose">
                  <div className="kpi-top">
                    <span className="kpi-ico i-rose">
                      <i className="bi bi-arrow-counterclockwise"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.returnedPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Returned</div>
                  <div className="kpi-val">
                    {kpiStats.returned.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">RTO in progress</div>
                  <div className="kpi-spark">
                    <canvas id="ks8"></canvas>
                  </div>
                </div>
              </div>
              <div className="kpi-cell">
                <div className="card-base kpi-card kpi-tint-indigo">
                  <div className="kpi-top">
                    <span className="kpi-ico i-indigo">
                      <i className="bi bi-x-circle"></i>
                    </span>
                    <span className="kpi-delta d-flat">
                      {kpiStats.cancelledPercentage}
                    </span>
                  </div>
                  <div className="kpi-lbl">Cancelled</div>
                  <div className="kpi-val">
                    {kpiStats.cancelled.toLocaleString("en-IN")}
                  </div>
                  <div className="kpi-sub">This period</div>
                  <div className="kpi-spark">
                    <canvas id="ks9"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-base ss-card mb-3">
              <div className="dash-head">
                <div>
                  <div className="dash-title">
                    <span
                      className="dash-ico"
                      style={{
                        background: "rgba(59,130,246,.12)",
                        color: "#3b82f6",
                      }}
                    >
                      <i className="bi bi-lightning-charge"></i>
                    </span>{" "}
                    Start Shipping
                  </div>
                  <div className="dash-sub">
                    Create orders manually or sync from your store
                  </div>
                </div>
              </div>
              <div className="ss-row">
                <div className="ss-option">
                  <div className="ss-opt-top">
                    <span className="ss-opt-ico">
                      <i className="bi bi-plug"></i>
                    </span>
                    <div>
                      <div className="ss-opt-title">
                        Start shipping orders in just <span>2 minutes</span>
                      </div>
                      <div className="ss-opt-sub">
                        Create single orders quickly without any store
                        integration. Enter buyer details, pick a courier, and
                        you're live.
                      </div>
                    </div>
                  </div>
                  <div className="ss-opt-foot">
                    <a href="/create_order" className="ss-btn ss-btn-primary">
                      <i className="bi bi-plus-circle"></i> Create Order
                    </a>
                  </div>
                </div>
                <div className="ss-divider"></div>
                <div className="ss-option">
                  <div className="ss-opt-top">
                    <span className="ss-opt-ico">
                      <i className="bi bi-shop"></i>
                    </span>
                    <div>
                      <div className="ss-opt-title">
                        Sync orders automatically from your{" "}
                        <span>favorite platform</span>
                      </div>
                      <div className="ss-opt-sub">
                        Connect your store and auto-import orders into
                        KalpCargo. No manual entry, no missed shipments.
                      </div>
                    </div>
                  </div>
                  <div className="ss-opt-foot">
                    <div className="ss-plat">
                      <span className="ss-plat-caption">Works with</span>
                      <img
                        src="assets/img/shopify_v.png"
                        alt="Shopify"
                        title="Shopify"
                      />
                      <img
                        src="assets/img/woo.png"
                        alt="WooCommerce"
                        title="WooCommerce"
                      />
                    </div>
                    <a href="/channel_list" className="ss-btn ss-btn-primary">
                      <i className="bi bi-link-45deg"></i> Connect Store
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-xl-6">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(16,185,129,.12)",
                            color: "#059669",
                          }}
                        >
                          <i className="bi bi-filter-left"></i>
                        </span>{" "}
                        Delivery Funnel
                      </div>
                      <div className="dash-sub">
                        Shipment lifecycle progression
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: "auto" }}>
                    <div className="tower-wrap">
                      {getDeliveryFunnel().map((row, index) => (
                        <div className="tower-col" key={row.name}>
                          <div className="tower-val">
                            {row.value.toLocaleString("en-IN")}
                          </div>
                          <div className="tower-track">
                            <div
                              className={`tower-fill g${index + 1}`}
                              style={{ width: `${row.percentage}%` }}
                            ></div>
                          </div>
                          <div className="tower-name">
                            {row.name}
                            <span>{row.percentage}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(245,158,11,.14)",
                            color: "#d97706",
                          }}
                        >
                          <i className="bi bi-cash-coin"></i>
                        </span>{" "}
                        Shipping Cost Trend
                      </div>
                      <div className="dash-sub">Average cost per shipment</div>
                    </div>
                    <div className="rank-val" style={{ fontSize: "20px" }}>
                      {money(getCostData().avg)}{" "}
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "var(--t3)",
                        }}
                      >
                        avg/order
                      </span>
                    </div>
                  </div>
                  <div
                    className="chart-box"
                    style={{ height: "220px", marginTop: "auto" }}
                  >
                    <canvas id="costChart"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-xl-4">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(16,185,129,.12)",
                            color: "#059669",
                          }}
                        >
                          <i className="bi bi-cash-coin"></i>
                        </span>{" "}
                        COD Analytics
                      </div>
                      <div className="dash-sub">Derived from COD shipments</div>
                    </div>
                  </div>
                  <div style={{ marginTop: "auto" }}>
                    <div className="cod-grid">
                      <div className="cod-tile">
                        <div className="cod-ico i-c">
                          <i className="bi bi-arrow-down-circle"></i>
                        </div>
                        <b>{compactMoney(getCodAnalytics().collected)}</b>
                        <span>Collected</span>
                      </div>
                      <div className="cod-tile">
                        <div className="cod-ico i-p">
                          <i className="bi bi-hourglass-split"></i>
                        </div>
                        <b>{compactMoney(getCodAnalytics().pending)}</b>
                        <span>Pending</span>
                      </div>
                      <div className="cod-tile">
                        <div className="cod-ico i-s">
                          <i className="bi bi-check-circle"></i>
                        </div>
                        <b>{compactMoney(getCodAnalytics().settled)}</b>
                        <span>Settled</span>
                      </div>
                    </div>
                    <div className="cod-note">
                      <i className="bi bi-info-circle"></i> Settled is based on{" "}
                      <b>cod_paid</b> shipment data.
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(124,111,207,.14)",
                            color: "#7c6fcf",
                          }}
                        >
                          <i className="bi bi-box"></i>
                        </span>{" "}
                        Weight Distribution
                      </div>
                      <div className="dash-sub">Shipments by parcel weight</div>
                    </div>
                  </div>
                  <div style={{ marginTop: "auto" }}>
                    {getWeightDistribution().map((row) => (
                      <div className="rank-row" key={row.name}>
                        <div className="rank-top">
                          <span className="rank-name">{row.name}</span>
                          <span className="rank-val">{row.percentage}%</span>
                        </div>
                        <div className="rank-track">
                          <div
                            className="rank-fill f-c2"
                            style={{ width: `${row.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(239,68,68,.12)",
                            color: "#dc2626",
                          }}
                        >
                          <i className="bi bi-exclamation-octagon"></i>
                        </span>{" "}
                        NDR Summary
                      </div>
                      <div className="dash-sub">
                        Non-delivery report reasons
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: "auto" }}>
                    <div className="ndr-grid">
                      {[
                        ["Customer Unreachable", "person-x", "n-unreach"],
                        ["Address Issue", "geo-alt", "n-addr"],
                        ["Rescheduled", "calendar-event", "n-resched"],
                        ["Rejected", "x-circle", "n-rej"],
                      ].map(([name, icon, cls]) => (
                        <div className="ndr-tile" key={name}>
                          <span className={`ndr-ico ${cls}`}>
                            <i className={`bi bi-${icon}`}></i>
                          </span>
                          <div>
                            <b>{getNdrSummary()[name]}</b>
                            <span>{name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <a href="/ndr" className="ndr-more-link">
                      <i className="bi bi-box-arrow-up-right"></i> Review all
                      NDRs
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12">
                <div className="help-title-bar">
                  <div>
                    <div className="dash-title">
                      Need help setting things up?
                    </div>
                    <div className="dash-sub">
                      Guides, videos and support to get you shipping faster.
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="card-base help-card">
                  <span className="help-ico h-support">
                    <i className="bi bi-headset"></i>
                  </span>
                  <div className="help-title">Talk to us</div>
                  <div className="help-desc">
                    Ask questions about shipments, orders, or anything.
                  </div>
                  <a href="/support" className="help-link">
                    Talk to support <i className="bi bi-arrow-right"></i>
                  </a>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="card-base help-card">
                  <span className="help-ico h-videos">
                    <i className="bi bi-play-circle"></i>
                  </span>
                  <div className="help-title">Start Watching</div>
                  <div className="help-desc">
                    Pro tips to help you ship with more confidence.
                  </div>
                  <a href="#" className="help-link">
                    Watch Videos <i className="bi bi-arrow-right"></i>
                  </a>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="card-base help-card">
                  <span className="help-ico h-blogs">
                    <i className="bi bi-journal-richtext"></i>
                  </span>
                  <div className="help-title">Read now</div>
                  <div className="help-desc">
                    Blogs that help you figure things out at your own pace.
                  </div>
                  <a href="#" className="help-link">
                    Read helpful blogs <i className="bi bi-arrow-right"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div
            id="viewAnalytics"
            style={{ display: activeView === "analytics" ? "block" : "none" }}
          >
            <div className="row g-3 mb-3">
              <div className="col-xl-12">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(245,158,11,.14)",
                            color: "#d97706",
                          }}
                        >
                          <i className="bi bi-graph-up-arrow"></i>
                        </span>{" "}
                        Performance Overview
                      </div>
                      <div className="dash-sub">Daily shipment movement</div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <div className="filter-pills">
                        {[
                          ["all", "All"],
                          ["prepaid", "Prepaid"],
                          ["cod", "COD"],
                        ].map(([key, label]) => (
                          <button
                            key={key}
                            type="button"
                            className={`fp ${perfType === key ? "on" : ""}`}
                            onClick={() => setPerfType(key)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <select
                        className="fp-select"
                        value={perfCarrier}
                        onChange={(e) => setPerfCarrier(e.target.value)}
                      >
                        <option value="all">All Carriers</option>
                        {Array.from(new Set(shipments.map(getCarrier)))
                          .filter(Boolean)
                          .map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                      </select>
                      <select
                        className="fp-select"
                        value={perfWarehouse}
                        onChange={(e) => setPerfWarehouse(e.target.value)}
                      >
                        <option value="all">All Warehouses</option>
                        {Array.from(
                          new Map(
                            shipments.map((s) => [
                              getWarehouseKey(s),
                              getWarehouseLabel(s),
                            ])
                          )
                        ).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="chart-box" style={{ height: "260px" }}>
                    <canvas id="anPerfLine"></canvas>
                  </div>
                  <div className="an-table-scroll mt-3">
                    <table className="table an-table">
                      <thead>
                        <tr>
                          <th>DATE</th>
                          <th>SHIPMENTS BOOKED</th>
                          <th>DELIVERED</th>
                          <th>RUNNING</th>
                          <th>RETURN TO ORIGIN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(
                          getDailyKpiData(getFilteredAnalyticsShipments())
                        ).map(([date, row]) => (
                          <tr key={date}>
                            <td>{formatDate(date)}</td>
                            <td>{row.total}</td>
                            <td>
                              {row.delivered} ({pct(row.delivered, row.total)})
                            </td>
                            <td>
                              {row.pickedUp +
                                row.inTransit +
                                row.outForDelivery}{" "}
                              (
                              {pct(
                                row.pickedUp +
                                  row.inTransit +
                                  row.outForDelivery,
                                row.total
                              )}
                              )
                            </td>
                            <td>
                              {row.returned} ({pct(row.returned, row.total)})
                            </td>
                          </tr>
                        ))}
                        {!dashboardData.length && (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No data for selected period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-xl-7">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(59,130,246,.12)",
                            color: "#3b82f6",
                          }}
                        >
                          <i className="bi bi-receipt"></i>
                        </span>{" "}
                        Order Summary
                      </div>
                      <div className="dash-sub">
                        COD vs prepaid split for the period
                      </div>
                    </div>
                  </div>
                  <div className="an-table-scroll">
                    <table className="table an-table">
                      <thead>
                        <tr>
                          <th>SNO.</th>
                          <th>CARRIERS</th>
                          <th>COD</th>
                          <th>PREPAID</th>
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaymentSummary().rows.map((r, i) => (
                          <tr key={r.name}>
                            <td>{i + 1}</td>
                            <td>{r.name}</td>
                            <td>{r.cod}</td>
                            <td>{r.prepaid}</td>
                            <td>{r.cod + r.prepaid}</td>
                          </tr>
                        ))}
                        <tr className="an-total">
                          <td></td>
                          <td>Total</td>
                          <td>
                            {getPaymentSummary().cod} (
                            {pct(
                              getPaymentSummary().cod,
                              getPaymentSummary().total
                            )}
                            )
                          </td>
                          <td>
                            {getPaymentSummary().prepaid} (
                            {pct(
                              getPaymentSummary().prepaid,
                              getPaymentSummary().total
                            )}
                            )
                          </td>
                          <td>{getPaymentSummary().total}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-xl-5">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(16,185,129,.12)",
                            color: "#059669",
                          }}
                        >
                          <i className="bi bi-pie-chart"></i>
                        </span>{" "}
                        COD vs Prepaid
                      </div>
                      <div className="dash-sub">Order payment split</div>
                    </div>
                  </div>
                  <div className="chart-box" style={{ height: "230px" }}>
                    <canvas id="anOrderDonut"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-xl-12">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(124,111,207,.14)",
                            color: "#7c6fcf",
                          }}
                        >
                          <i className="bi bi-truck"></i>
                        </span>{" "}
                        Carrier Overview
                      </div>
                      <div className="dash-sub">Performance by carrier</div>
                    </div>
                  </div>
                  <div className="an-table-scroll">
                    <table className="table an-table">
                      <thead>
                        <tr>
                          <th>SNO.</th>
                          <th>Carrier NAME</th>
                          <th>SHIPMENTS BOOKED</th>
                          <th>DELIVERED</th>
                          <th>RUNNING</th>
                          <th>RETURN TO ORIGIN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupOverview("carrier").map((r, i) => (
                          <tr key={r.name}>
                            <td>{i + 1}</td>
                            <td>{r.name}</td>
                            <td>{r.booked}</td>
                            <td>
                              {r.delivered} ({pct(r.delivered, r.booked)})
                            </td>
                            <td>
                              {r.running} ({pct(r.running, r.booked)})
                            </td>
                            <td>
                              {r.rto} ({pct(r.rto, r.booked)})
                            </td>
                          </tr>
                        ))}
                        <tr className="an-total">
                          <td></td>
                          <td>Total</td>
                          <td>
                            {groupOverview("carrier").reduce(
                              (s, r) => s + r.booked,
                              0
                            )}
                          </td>
                          <td>
                            {groupOverview("carrier").reduce(
                              (s, r) => s + r.delivered,
                              0
                            )}
                          </td>
                          <td>
                            {groupOverview("carrier").reduce(
                              (s, r) => s + r.running,
                              0
                            )}
                          </td>
                          <td>
                            {groupOverview("carrier").reduce(
                              (s, r) => s + r.rto,
                              0
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {[
              ["state", "State Wise Overview", "map"],
              ["city", "City Wise Overview", "building"],
              ["zone", "Zone Wise Overview", "grid-3x3"],
            ]
              .reduce((rows, item, idx) => {
                if (idx % 2 === 0) rows.push([]);
                rows[rows.length - 1].push(item);
                return rows;
              }, [])
              .map((pair, pi) => (
                <div className="row g-3 mb-3" key={pi}>
                  {pair.map(([field, title, icon]) => (
                    <div className="col-xl-6" key={field}>
                      <div className="card-base dash-card">
                        <div className="dash-head">
                          <div>
                            <div className="dash-title">
                              <span
                                className="dash-ico"
                                style={{
                                  background: "rgba(20,184,166,.12)",
                                  color: "#0d9488",
                                }}
                              >
                                <i className={`bi bi-${icon}`}></i>
                              </span>{" "}
                              {title}
                            </div>
                            <div className="dash-sub">By {field}</div>
                          </div>
                        </div>
                        <div className="an-table-scroll">
                          <table className="table an-table">
                            <thead>
                              <tr>
                                <th>{field.toUpperCase()} NAME</th>
                                <th>BOOKED</th>
                                <th>DELIVERED</th>
                                <th>RUNNING</th>
                                <th>RTO</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupOverview(field).map((r) => (
                                <tr key={r.name}>
                                  <td>{r.name}</td>
                                  <td>{r.booked}</td>
                                  <td>
                                    {r.delivered} ({pct(r.delivered, r.booked)})
                                  </td>
                                  <td>
                                    {r.running} ({pct(r.running, r.booked)})
                                  </td>
                                  <td>
                                    {r.rto} ({pct(r.rto, r.booked)})
                                  </td>
                                </tr>
                              ))}
                              <tr className="an-total">
                                <td>Total</td>
                                <td>
                                  {groupOverview(field).reduce(
                                    (s, r) => s + r.booked,
                                    0
                                  )}
                                </td>
                                <td>
                                  {groupOverview(field).reduce(
                                    (s, r) => s + r.delivered,
                                    0
                                  )}
                                </td>
                                <td>
                                  {groupOverview(field).reduce(
                                    (s, r) => s + r.running,
                                    0
                                  )}
                                </td>
                                <td>
                                  {groupOverview(field).reduce(
                                    (s, r) => s + r.rto,
                                    0
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

            <div className="row g-3 mb-3">
              <div className="col-xl-6">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(124,111,207,.14)",
                            color: "#7c6fcf",
                          }}
                        >
                          <i className="bi bi-trophy"></i>
                        </span>{" "}
                        Top 2
                      </div>
                      <div className="dash-sub">
                        Top cities & states by volume
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-4">
                    {[
                      ["Top City", "city"],
                      ["Top State", "state"],
                    ].map(([label, field]) => (
                      <div key={field}>
                        <div className="an-top-label">{label}</div>
                        {groupOverview(field)
                          .slice(0, 2)
                          .map((r) => (
                            <div className="rank-row" key={r.name}>
                              <div className="rank-top">
                                <span className="rank-name">{r.name}</span>
                                <span className="rank-val">
                                  {r.booked} (
                                  {pct(
                                    r.booked,
                                    groupOverview(field).reduce(
                                      (s, x) => s + x.booked,
                                      0
                                    )
                                  )}
                                  )
                                </span>
                              </div>
                              <div className="rank-track">
                                <div
                                  className="rank-fill f-c1"
                                  style={{
                                    width: `${pct(
                                      r.booked,
                                      groupOverview(field).reduce(
                                        (s, x) => s + x.booked,
                                        0
                                      )
                                    ).replace("%", "")}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="card-base dash-card">
                  <div className="dash-head">
                    <div>
                      <div className="dash-title">
                        <span
                          className="dash-ico"
                          style={{
                            background: "rgba(59,130,246,.12)",
                            color: "#3b82f6",
                          }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>{" "}
                        Data Coverage
                      </div>
                      <div className="dash-sub">Selected dashboard period</div>
                    </div>
                  </div>
                  <div className="rank-row">
                    <div className="rank-top">
                      <span className="rank-name">Shipments</span>
                      <span className="rank-val">
                        {shipments.length.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  <div className="rank-row">
                    <div className="rank-top">
                      <span className="rank-name">Sales value</span>
                      <span className="rank-val">
                        {compactMoney(getPeriodMetrics().sales)}
                      </span>
                    </div>
                  </div>
                  <div className="rank-row">
                    <div className="rank-top">
                      <span className="rank-name">COD orders</span>
                      <span className="rank-val">
                        {shipments
                          .filter((s) => getPaymentMode(s) === "cod")
                          .length.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                  <div className="rank-row">
                    <div className="rank-top">
                      <span className="rank-name">Transactions</span>
                      <span className="rank-val">
                        {transactionData.length.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Header>
      <Footer />
    </div>
  );
};

export default Index;
